import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "../hooks/useSEO";
import { AdSlot } from "../components/AdSlot";

const CALCULATORS = [
  { slug: "no-tax-on-tips", title: "No Tax on Tips Calculator", summary: "OBBBA's new $25,000 tips deduction. See exactly what you qualify for based on your income and filing status.", tags: ["NEW", "OBBBA"] },
  { slug: "no-tax-on-overtime", title: "No Tax on Overtime Calculator", summary: "Up to $12,500 (single) or $25,000 (joint) overtime deduction for non-exempt FLSA employees.", tags: ["NEW", "OBBBA"] },
  { slug: "senior-deduction-2026", title: "Senior $6,000 Deduction Calculator", summary: "Extra above-the-line deduction for taxpayers 65+, with MAGI-based phase-outs.", tags: ["NEW", "Seniors"] },
  { slug: "auto-loan-interest-2026", title: "Auto Loan Interest Calculator", summary: "Deduct up to $10,000/year of interest on qualified U.S.-assembled new-vehicle auto loans.", tags: ["NEW", "OBBBA"] },
  { slug: "salt-deduction-2026", title: "$40,000 SALT Cap Calculator", summary: "OBBBA raised the SALT cap to $40,000 with a phase-out above $500k MAGI. Calculate your real benefit.", tags: ["UPDATED"] },
  { slug: "tax-bracket-2026", title: "2026 Federal Tax Bracket Calculator", summary: "Compare 2025 vs 2026 federal income tax with the actual IRS brackets and OBBBA's extra inflation bump on lower brackets.", tags: ["Primary"] },
  { slug: "child-tax-credit-2026", title: "Child Tax Credit Calculator", summary: "Permanent $2,200 per child under OBBBA. Model phase-out scenarios for your household.", tags: ["Families"] },
  { slug: "standard-deduction-2026", title: "Standard Deduction Calculator", summary: "$16,100 (single) / $32,200 (joint) for 2026 — see how inflation adjustments help you.", tags: ["Deductions"] },
  { slug: "qbi-deduction-2026", title: "QBI Section 199A Calculator", summary: "The 20% pass-through deduction is now permanent. Calculate your 2026 QBI savings.", tags: ["Business"] },
  { slug: "capital-gains-2026", title: "Capital Gains Tax Calculator", summary: "Long-term capital gains at 0%, 15%, or 20% with updated 2026 thresholds and NIIT.", tags: ["Investments"] },
  { slug: "estate-tax-2026", title: "Estate Tax Exemption Calculator", summary: "OBBBA permanently raised the exemption to $15M single / $30M joint. Calculate your estate's exposure.", tags: ["Estate"] },
];

const CalculatorsHub = () => {
  useSEO({
    title: "All 11 OBBBA Tax Calculators — Free 2026 Tools",
    description: "Free calculators for every major OBBBA provision: tips, overtime, senior deduction, SALT, QBI, brackets, CTC, auto loan, capital gains, estate tax.",
    keywords: "obbba calculators, 2026 tax tools, tips overtime senior calculator, salt cap phase out",
    path: "/calculators",
  });

  return (
    <>
      <section className="border-b-2 border-foreground py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-4 block">
            The Calculator Suite
          </span>
          <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight mb-6" data-testid="calculators-hub-title">
            Eleven calculators covering every <span className="italic">OBBBA provision</span>.
          </h1>
          <p className="font-body text-lg text-foreground/80 max-w-3xl leading-relaxed">
            Based on Public Law 119-21 and official IRS Revenue Procedures. Updated annually as new inflation adjustments are released.
          </p>
        </div>
      </section>

      <AdSlot size="leaderboard" testid="hub-leaderboard-ad" />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-foreground/20">
            {CALCULATORS.map((c, i) => (
              <Link
                key={c.slug}
                to={`/calculator/${c.slug}`}
                className="group p-8 md:p-10 border-r border-b border-foreground/20 bg-card hover:bg-foreground hover:text-background transition-colors"
                data-testid={`hub-calc-${c.slug}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground group-hover:text-background/60">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex gap-1 flex-wrap justify-end">
                    {c.tags.map((t) => (
                      <span
                        key={t}
                        className={`font-mono text-[9px] uppercase tracking-widest border px-2 py-1 ${t === "NEW" ? "bg-primary text-primary-foreground border-primary group-hover:bg-primary-foreground group-hover:text-primary" : "border-foreground/30 group-hover:border-background/40"}`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4 leading-tight">
                  {c.title}
                </h2>
                <p className="font-body text-base text-foreground/80 group-hover:text-background/80 leading-relaxed mb-6">
                  {c.summary}
                </p>
                <div className="font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
                  Open Calculator <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          {/* State-level suite cross-link */}
          <div className="mt-16 border-2 border-foreground p-8 md:p-12 bg-primary/5" data-testid="states-callout">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="max-w-2xl">
                <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-3 block">
                  Coming in 2026
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-3 leading-tight">
                  Federal + state, <span className="italic">combined</span>.
                </h2>
                <p className="font-body text-base text-foreground/80 leading-relaxed">
                  Every OBBBA calculator above runs federal numbers. Your full
                  2026 bill also depends on where you live. Pick a state to see
                  the combined effective rate.
                </p>
              </div>
              <Link
                to="/states"
                className="font-mono uppercase text-xs tracking-widest bg-foreground text-background px-6 py-4 inline-flex items-center gap-2 hover:bg-primary transition-colors whitespace-nowrap"
                data-testid="cta-states"
              >
                Browse all 50 states <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CalculatorsHub;
